/* DealProductLines: a cargo deal's several products, one row each, with its
 * own quantity and price, and the total and average underneath (go-live card
 * 1248h535zfw, Chris's walk-through 2026-10-20; hand-ported from HMS). Used by
 * the Bid/Offer and the Trade Recap.
 *
 *   <DealProductLines
 *     lines={lines} onChange={setLines}
 *     keys={OFFER_LINE_KEYS}            // or recapLineKeys(recapType)
 *     unit="MT" currency="USD" priceLabel="Price"
 *   />
 *
 * The deal's one unit and one currency apply to every row (Chris's call).
 * Removing a row down to one hands the remaining row back through
 * `onChange([row])`: the screen then goes back to its single Product box.
 * The total and average shown here are for reading while typing; the server
 * works out the stored header itself (backend/offer/product_lines.py).
 */
import React from 'react';
import RecapProductSearch from './RecapProductSearch';
import AmountInput from './ui/AmountInput';
import HMSButton from './ui/HMSButton';
import { PRICE_DECIMALS, QUANTITY_DECIMALS } from '../utils/precision';
import { blankLine, lineProductLabel, linesHeader } from '../utils/dealProductLines';
import './DealProductLines.css';

const DealProductLines = ({
  lines,
  onChange,
  keys,
  unit = '',
  currency = '',
  priceLabel = 'Price',
  disabled = false,
}) => {
  const rows = Array.isArray(lines) ? lines : [];
  const { quantityKey, priceKey } = keys;
  const { total, average } = linesHeader(rows, keys);

  const update = (index, patch) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const remove = (index) => onChange(rows.filter((_, i) => i !== index));
  const add = () => onChange([...rows, blankLine(keys)]);

  const unitText = unit ? ` (${unit})` : '';
  const currencyText = currency ? ` (${currency})` : '';

  return (
    <div className="hms-deal-lines">
      <table className="hms-deal-lines__table">
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col" className="hms-deal-lines__num">{`Quantity${unitText}`}</th>
            <th scope="col" className="hms-deal-lines__num">{`${priceLabel}${currencyText}`}</th>
            <th scope="col" aria-label="Remove" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row._key ?? row.id ?? `line-${index}`}>
              <td className="hms-deal-lines__product">
                <RecapProductSearch
                  label=""
                  value={row.product_label}
                  variantId={row.stock_item_variant}
                  onChange={(variantId, displayName, variant) => update(index, {
                    stock_item_variant: variantId,
                    product_label: variantId ? lineProductLabel(displayName, variant) : '',
                  })}
                  disabled={disabled}
                />
              </td>
              <td className="hms-deal-lines__num">
                <AmountInput
                  className="hms-input"
                  aria-label={`Product ${index + 1} quantity`}
                  value={row[quantityKey] ?? ''}
                  maxDecimals={QUANTITY_DECIMALS}
                  onChange={(value) => update(index, { [quantityKey]: value })}
                  disabled={disabled}
                  placeholder="0"
                />
              </td>
              <td className="hms-deal-lines__num">
                <AmountInput
                  className="hms-input"
                  aria-label={`Product ${index + 1} ${priceLabel.toLowerCase()}`}
                  value={row[priceKey] ?? ''}
                  maxDecimals={PRICE_DECIMALS}
                  allowNegative
                  onChange={(value) => update(index, { [priceKey]: value })}
                  disabled={disabled}
                  placeholder="0.00"
                />
              </td>
              <td className="hms-deal-lines__remove">
                <HMSButton
                  variant="icon"
                  aria-label={`Remove product ${index + 1}`}
                  title="Remove this product"
                  onClick={() => remove(index)}
                  disabled={disabled}
                >
                  ×
                </HMSButton>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td className="hms-deal-lines__num">{total ? `${total}${unit ? ` ${unit}` : ''}` : '—'}</td>
            <td className="hms-deal-lines__num">
              {average ? `average ${currency ? `${currency} ` : ''}${average}` : '—'}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
      {!disabled && (
        <HMSButton variant="ghost" size="compact" onClick={add} className="hms-deal-lines__add">
          + Add another product
        </HMSButton>
      )}
    </div>
  );
};

export default DealProductLines;
